/**
 * Member Activation → KPIs (AR-328).
 *
 * Monitors activation performance across the active member base: headline
 * numbers, per-module load and pace, where members opt out, and the stalled
 * queue with its outreach state.
 */
import React from 'react';
import { Download } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  KPI_CARDS,
  KPI_MODULE_BARS,
  KPI_SKIP_ROWS,
  KPI_STALLED_COUNT,
  KPI_STALLED_FILTERS,
  KPI_STALLED_ROWS,
  KPI_TIME_BARS,
  OUTREACH_TONE_CLASSES,
  type KpiCard,
} from '@/lib/activationBuilder';

const CARD_TONE_CLASSES: Record<KpiCard['tone'], string> = {
  neutral: 'text-gray-900 dark:text-gray-100',
  orange: 'text-orange-600 dark:text-orange-400',
  teal: 'text-teal-600 dark:text-teal-400',
  red: 'text-red-700 dark:text-red-400',
};

const Panel: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({
  title,
  sub,
  children,
}) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
    {sub && <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{sub}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

const Bar: React.FC<{ pct: number; className?: string }> = ({ pct, className }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
    <div
      className={cn('h-full rounded-full', className ?? 'bg-orange-500')}
      style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
    />
  </div>
);

const MemberActivationKpisPage: React.FC = () => {
  const maxModuleCount = Math.max(...KPI_MODULE_BARS.map((m) => m.count));
  const maxDays = Math.max(...KPI_TIME_BARS.map((t) => t.days));
  const maxSkips = Math.max(...KPI_SKIP_ROWS.map((s) => s.count));

  return (
    <AdminLayout
      title="KPIs"
      description="Monitor member activation performance across the active member base."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {KPI_CARDS.map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900"
          >
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">
              {card.label}
            </p>
            <p className={cn('mt-2 text-3xl font-semibold', CARD_TONE_CLASSES[card.tone])}>
              {card.value}
            </p>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{card.sub}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Members currently in each module">
          <ul className="space-y-3">
            {KPI_MODULE_BARS.map((module) => (
              <li key={module.name}>
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <span className="truncate text-xs text-gray-700 dark:text-gray-200">
                    {module.name}
                  </span>
                  <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">
                    {module.count} in module · {module.completion}% complete
                  </span>
                </div>
                <Bar pct={(module.count / maxModuleCount) * 100} className={module.bar} />
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Average time spent per module">
          <ul className="space-y-3">
            {KPI_TIME_BARS.map((module) => (
              <li key={module.name}>
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <span className="truncate text-xs text-gray-700 dark:text-gray-200">
                    {module.name}
                  </span>
                  <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">
                    {module.days} days
                  </span>
                </div>
                <Bar pct={(module.days / maxDays) * 100} className="bg-violet-500" />
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <Panel title="Skips per optional step" sub="Where members opt out most">
          <ul className="space-y-3">
            {KPI_SKIP_ROWS.map((row) => (
              <li key={row.name} className="flex items-center gap-3">
                <span className="min-w-0 flex-1 truncate text-xs text-gray-700 dark:text-gray-200">
                  {row.name}
                </span>
                <span className="w-24 shrink-0">
                  <Bar pct={(row.count / maxSkips) * 100} />
                </span>
                <span className="w-8 shrink-0 text-right text-xs font-semibold text-gray-700 dark:text-gray-200">
                  {row.count}
                </span>
              </li>
            ))}
          </ul>
        </Panel>

        <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Stalled members
            </h3>
            <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              {KPI_STALLED_COUNT} flagged · inactive 7+ days
            </span>
            <Button variant="outline" size="sm" className="ml-auto">
              <Download className="mr-1 h-3.5 w-3.5" /> Export CSV
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {KPI_STALLED_FILTERS.map((filter) => (
              <Button key={filter} variant="outline" size="sm" className="h-7 text-[11px]">
                {filter}
              </Button>
            ))}
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-xs">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-wider text-gray-400">
                  <th className="py-2 pr-3 font-semibold">Member</th>
                  <th className="py-2 pr-3 font-semibold">Plan</th>
                  <th className="py-2 pr-3 font-semibold">Route</th>
                  <th className="py-2 pr-3 font-semibold">Stalled on</th>
                  <th className="py-2 pr-3 font-semibold">Days</th>
                  <th className="py-2 font-semibold">Outreach</th>
                </tr>
              </thead>
              <tbody>
                {KPI_STALLED_ROWS.map((row) => (
                  <tr key={row.member} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-2.5 pr-3 font-semibold text-gray-900 dark:text-gray-100">
                      {row.member}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300">{row.plan}</td>
                    <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300">{row.route}</td>
                    <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300">{row.step}</td>
                    <td className="py-2.5 pr-3 font-semibold text-orange-600 dark:text-orange-400">
                      {row.days}d
                    </td>
                    <td
                      className={cn(
                        'py-2.5 font-semibold',
                        OUTREACH_TONE_CLASSES[row.outreach] ?? 'text-gray-500',
                      )}
                    >
                      {row.outreach}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default MemberActivationKpisPage;
